import React, { useState, useEffect } from 'react';
import { HiChevronLeft, HiChevronRight } from 'react-icons/hi';
import './CongressTopics.css';
import { topicsService } from '../services/topicsService';
import { supabase } from '../supabaseClient';

function CongressTopics({ edition: customEdition }) {
  const [topicsData, setTopicsData] = useState({
    editionInfo: {
      editionNumber: '',
      themeTitle: '',
      themeSubtitle: ''
    },
    congressHeader: {
      title: 'Axes du Congrès',
      subtitle: ''
    },
    topics: []
  });
  const [currentPage, setCurrentPage] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  const topicsPerPage = 4;
  const totalPages = Math.ceil(topicsData.topics.length / topicsPerPage);

  useEffect(() => {
    loadTopicsData();
  }, [customEdition]);

  const loadTopicsData = async () => {
    try {
      setLoading(true);
      
      let editionYear = customEdition;
      if (!editionYear) {
        // Get active edition first
        const { data: activeEdition, error: editionError } = await supabase
          .from('editions')
          .select('year')
          .eq('is_active', true)
          .single();
        editionYear = activeEdition?.year || '2025';
      }

      const data = await topicsService.getTopicsData(editionYear);
      
      if (data) {
        setTopicsData(data);
      }
      setError(null);
    } catch (error) {
      setError('Failed to load topics data');
    } finally {
      setLoading(false);
    }
  };

  const getCurrentTopics = () => {
    const startIndex = currentPage * topicsPerPage;
    return topicsData.topics.slice(startIndex, startIndex + topicsPerPage);
  };

  const nextPage = () => {
    setCurrentPage((prev) => (prev + 1) % totalPages);
  };

  const prevPage = () => {
    setCurrentPage((prev) => (prev - 1 + totalPages) % totalPages);
  };

  if (loading) {
    return (
      <div className="congress-topics-page">
        <div className="congress-topics-container">
          <div className="congress-topics-header">
                {/* Replace text with animated loading spinner */}
              <div className="loading-spinner"></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="congress-topics-page">
      <div className="congress-topics-container">
        <div className="congress-edition-banner">
          <div className="edition-info">
            <h2 className="edition-number">{topicsData.editionInfo.editionNumber}</h2>
            <h3 className="theme-text">
              {topicsData.editionInfo.themeTitle}
            </h3>
          </div>
          <div className="theme-section">
            <h4 className="theme-text"></h4>
            <p className="theme-text">
              "{topicsData.editionInfo.themeSubtitle}"
            </p>
          </div>
        </div>

        <div className="congress-topics-header">
          <h1>{topicsData.congressHeader.title}</h1>
          <p>{topicsData.congressHeader.subtitle}</p>
        </div>
        
        <div className="congress-topics-content">
          {topicsData.topics.length > 0 ? (
            <>
              <div className="topics-grid-container">
                <div className="topics-grid">
                  {getCurrentTopics().map((topic, index) => (
                    <div 
                      key={topic.id} 
                      className={`topic-card ${index % 2 === 0 ? 'left-align' : 'right-align'}`}
                      style={{ '--topic-color': topic.color }}
                    >
                      <div className="topic-icon">
                        {topic.number}
                      </div>
                      <div className="topic-content">
                        <h3>{topic.title}</h3>
                        <p>{topic.description}</p>
                      </div>
                      <div className="topic-connector"></div>
                    </div>
                  ))}
                </div>
              </div>
              
              {totalPages > 1 && (
                <div className="topics-navigation responsive-topics-navigation">
                  <button 
                    className="topics-nav-btn prev" 
                    onClick={prevPage}
                    disabled={totalPages <= 1}
                    aria-label="Page précédente"
                  >
                    <HiChevronLeft />
                  </button>
                  
                  <div className="topics-page-indicators responsive-page-indicators" tabIndex={0} aria-label="Page actuelle">
                    <span className="topics-page-number">
                      {currentPage + 1} / {totalPages}
                    </span>
                  </div>
                  
                  <button 
                    className="topics-nav-btn next" 
                    onClick={nextPage}
                    disabled={totalPages <= 1}
                    aria-label="Page suivante"
                  >
                    <HiChevronRight />
                  </button>
                </div>
              )}
            </>
          ) : (
            <div className="no-topics-message">
              <p>Aucun axe de recherche disponible pour le moment.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default CongressTopics;
                          